import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  Crown,
  Code,
  Headphones,
  Calendar,
  Mail,
  Phone,
  ArrowRight,
  Database,
  UserPlus,
  Eye,
  ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import AdminUserEditor from './AdminUserEditor';

/**
 * Componente de Gestão de Usuários (Admin)
 * Permite administradores gerenciar usuários do sistema (Cadastrados e Pré-cadastrados)
 */
const AdminUserManagement = () => {
  const { allUsers, refreshAllUsers, preRegisteredStaff, startViewAsUser } = useAuth();
  const { tickets } = useTickets();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingUser, setEditingUser] = useState(null);

  const handleImpersonate = async (userId) => {
    if (confirm('Deseja realmente visualizar o painel como este usuário?')) {
      const result = await startViewAsUser(userId);
      if (result.success) {
        if (result.isPreRegistered) {
          navigate('/staff-register?mode=view');
        } else {
          navigate('/dashboard');
        }
      } else {
        alert(result.error);
      }
    }
  };
  // Combine registered users and pre-registered staff
  const combinedUsers = [
    // Registered users
    ...(allUsers || []),
    // Pre-registered staff (mapped to match user structure)
    ...Object.values(preRegisteredStaff || {}).map(pre => ({
      id: `pre_${pre.discordId}`,
      discordId: pre.discordId,
      name: pre.codename || 'Pré-Cadastro',
      email: 'Aguardando Registro',
      accessLevel: pre.accessLevel,
      registrationStatus: 'pre_registered',
      lastLogin: null,
      preStaffData: pre, // Keep original data accessible
      cities: []
    }))
  ];

  // Enriquecer dados dos usuários com contagem de tickets
  const usersWithStats = combinedUsers.map(user => ({
    ...user,
    ticketsCount: tickets.filter(t => t.userDiscordId === user.discordId).length
  }));



  // Função para obter cor do nível de acesso
  const getAccessLevelBadge = (level) => {
    const badges = {
      'user': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: UserCheck, label: 'Usuário' },
      'suporte': { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Headphones, label: 'Suporte' },
      'suporte_compras': { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Headphones, label: 'Suporte Compras' },
      'suporte_descontos': { color: 'bg-teal-500/20 text-teal-400 border-teal-500/30', icon: Headphones, label: 'Suporte Descontos' },
      'moderador': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Shield, label: 'Moderador' },
      'super_moderador': { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Shield, label: 'Super Moderador' },
      'programador': { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Code, label: 'Programador' },
      'head_suporte': { color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', icon: Crown, label: 'Head Suporte' },
      'head_moderacao': { color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: Crown, label: 'Head Moderação' },
      'head_staff': { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: Crown, label: 'Head Staff' },
      'analista_qualidade': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: ShieldCheck, label: 'Analista de Qualidade' },
      'departamento_juridico': { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: ShieldCheck, label: 'Departamento Jurídico' },
      'criador_de_conteudo': { color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', icon: Crown, label: 'Criador de Conteúdo' },
      'admin': { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Crown, label: 'Admin' }
    };
    return badges[level] || badges.user;
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtra usuários
  const filteredUsers = usersWithStats.filter(user => {
    const term = searchTerm.toLowerCase();
    const nameMatch = user.name?.toLowerCase().includes(term);
    const discordMatch = user.discordId?.includes(searchTerm);
    const emailMatch = user.email?.toLowerCase().includes(term);

    // Busca por ID de Personagem dentro do array de cidades
    const charIdMatch = user.cities?.some(c =>
      c.playerId?.toLowerCase().includes(term) ||
      c.characterName?.toLowerCase().includes(term)
    );

    const matchesSearch = nameMatch || discordMatch || emailMatch || charIdMatch;

    const matchesLevel = filterLevel === 'all' || user.accessLevel === filterLevel;
    const matchesStatus = filterStatus === 'all' || user.registrationStatus === filterStatus;

    return matchesSearch && matchesLevel && matchesStatus;
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent flex items-center gap-3">
            <Users className="h-10 w-10 text-blue-500" />
            Gestão de Perfis
          </h1>
          <p className="text-gray-400 mt-2 text-lg">
            Visualize e gerencie todos os usuários cadastrados e pré-cadastrados
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Botões removidos conforme solicitado */}
        </div>
      </div>

      {/* Filtros Avançados / Busca Moderna */}
      <Card className="bg-black/40 border-white/10 backdrop-blur-md overflow-hidden ring-1 ring-white/5">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />
        <CardContent className="p-8 relative z-10 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Localizar Usuário</h2>
              <p className="text-gray-400">Busque por ID do Discord, E-mail, Nome ou ID do Personagem</p>
            </div>

            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-25 group-focus-within:opacity-50 transition duration-1000"></div>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-500" />
                <Input
                  placeholder="Ex: 401033..., joao@email.com, João ou ID123"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 bg-gray-950/80 border-white/10 text-white placeholder-gray-600 h-14 rounded-2xl focus:ring-blue-500/50 text-lg shadow-2xl"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-3 bg-white/5 p-1 rounded-xl border border-white/5">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-transparent border-none text-white w-[180px] h-10">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10 text-white">
                    <SelectItem value="all">Todos os Status</SelectItem>
                    <SelectItem value="registered">Cadastrados</SelectItem>
                    <SelectItem value="pre_registered">Pré-Cadastrados</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-[1px] h-6 bg-white/10" />
                <Select value={filterLevel} onValueChange={setFilterLevel}>
                  <SelectTrigger className="bg-transparent border-none text-white w-[180px] h-10">
                    <SelectValue placeholder="Cargo" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10 text-white">
                    <SelectItem value="all">Todos os Cargos</SelectItem>
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="suporte">Suporte</SelectItem>
                    <SelectItem value="suporte_compras">Suporte Compras</SelectItem>
                    <SelectItem value="suporte_descontos">Suporte Descontos</SelectItem>
                    <SelectItem value="moderador">Moderador</SelectItem>
                    <SelectItem value="super_moderador">Super Moderador</SelectItem>
                    <SelectItem value="programador">Programador</SelectItem>
                    <SelectItem value="head_suporte">Head Suporte</SelectItem>
                    <SelectItem value="head_moderacao">Head Moderação</SelectItem>
                    <SelectItem value="head_staff">Head Staff</SelectItem>
                    <SelectItem value="analista_qualidade">Analista de Qualidade</SelectItem>
                    <SelectItem value="departamento_juridico">Departamento Jurídico</SelectItem>
                    <SelectItem value="criador_de_conteudo">Criador de Conteúdo</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid de Usuários ou Estado Vazio */}
      <div className="grid grid-cols-1 gap-4">
        {searchTerm.length >= 2 ? (
          <>
            {filteredUsers.map((user, index) => {
              const badge = getAccessLevelBadge(user.accessLevel);
              const Icon = badge.icon;
              const isPreReg = user.registrationStatus === 'pre_registered';

              return (
                <motion.div
                  key={user.discordId || user.id || index}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="group"
                >
                  <Card className={`bg-black/40 border-white/5 hover:border-white/20 transition-all duration-300 relative overflow-hidden ${isPreReg ? 'border-l-4 border-l-purple-500/50' : 'border-l-4 border-l-blue-500/50'}`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                      <div className="flex items-center gap-5">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center p-0.5 ${isPreReg ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gradient-to-br from-blue-500 to-cyan-600'}`}>
                          <div className="w-full h-full bg-gray-950 rounded-[14px] flex items-center justify-center">
                            <Icon className={`h-7 w-7 ${isPreReg ? 'text-purple-400' : 'text-blue-400'}`} />
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                              {user.name}
                            </h3>
                            <Badge className={`${badge.color} border-none`}>
                              {badge.label}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-1 gap-x-6 mt-2 text-sm text-gray-500 font-medium">
                            <div className="flex items-center gap-2">
                              <Users className="h-3.5 w-3.5" />
                              <span>{user.discordId}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5" />
                              <span className="truncate max-w-[150px]">{user.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>Acesso: {formatDate(user.lastLogin)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-blue-400/60">
                              <Shield className="h-3.5 w-3.5" />
                              <span>{user.ticketsCount} Atendimentos</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 justify-end">
                        <Button
                          onClick={() => handleImpersonate(user.discordId || user.id)}
                          className="h-12 w-12 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-all hover:scale-105 active:scale-95 flex items-center justify-center p-0"
                          title="Visualizar como este usuário"
                        >
                          <Eye className="h-5 w-5" />
                        </Button>

                        <Button
                          onClick={() => setEditingUser(user)}
                          className={`h-12 px-6 rounded-xl font-bold flex items-center gap-2 transition-all group-hover:scale-105 active:scale-95 ${isPreReg ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-900/20' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-900/20'}`}
                        >
                          Gerenciar Usuário
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}

            {filteredUsers.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20 bg-black/20 rounded-3xl border border-dashed border-white/10"
              >
                <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="h-10 w-10 text-gray-600" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Nenhum resultado para "{searchTerm}"
                </h3>
                <p className="text-gray-500 max-w-xs mx-auto">
                  Tente buscar por outro termo ou remova os filtros de cargo/status.
                </p>
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-32 bg-black/20 rounded-[32px] border border-dashed border-white/5 backdrop-blur-sm"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-8 ring-1 ring-white/10">
              <Search className="h-12 w-12 text-blue-500/50" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">
              Inicie uma pesquisa
            </h3>
            <p className="text-gray-400 max-w-md mx-auto text-lg">
              Para garantir a melhor performance do sistema, digite pelo menos 2 caracteres para localizar um perfil.
            </p>
            <div className="flex items-center justify-center gap-6 mt-8">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                Discord ID
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                E-mail
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 rounded-full bg-pink-500" />
                ID Personagem
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Alerta de Segurança */}
      <Alert className="bg-red-500/5 border-red-500/20 text-red-400/80 rounded-2xl p-6">
        <Shield className="h-5 w-5" />
        <AlertDescription className="ml-2 font-medium">
          <strong>Aviso de Segurança:</strong> Todas as alterações realizadas neste painel são registradas no log de auditoria permanente.
          A alteração de níveis de acesso requer permissão de Administrador nível 2.
        </AlertDescription>
      </Alert>

      {/* Editor de Perfil (Modal) */}
      <AnimatePresence>
        {editingUser && (
          <AdminUserEditor
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onSave={() => {
              refreshAllUsers();
              setEditingUser(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUserManagement;
